import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { Mail, MessageCircle, Send, CheckCircle2 } from "lucide-react";
import InfoPage from "../components/InfoPage";

const SUPPORT_EMAIL = "driveorkick@gmail.com";
const SUPPORT_WA = "919599921411"; // +91 prefix dropped for wa.me

export default function ContactUs() {
    const { user } = useAuth();
    const [form, setForm] = useState({
        name: user?.name || "",
        email: user?.email || "",
        message: "",
    });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (form.message.trim().length < 5) { toast.error("Please share a bit more in your message"); return; }
        setSending(true);
        try {
            await api.post("/contact", form);
            setSent(true);
            toast.success("Message sent — we'll get back to you soon");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not send. Try email/WhatsApp instead.");
        } finally {
            setSending(false);
        }
    };

    return (
        <InfoPage title="Contact Us" subtitle="We typically reply within 1 business day" testid="contact-page">
            <Toaster theme="dark" richColors position="top-center" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="contact-email-link"
                    className="flex items-center gap-3 bg-slate-950 border border-slate-800 rounded-lg p-4 hover:border-[#00FF66]/60 transition-colors">
                    <div className="w-10 h-10 rounded-full bg-[#00FF66]/10 border border-[#00FF66]/40 flex items-center justify-center">
                        <Mail className="w-4 h-4 text-[#00FF66]" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Email</div>
                        <div className="text-sm text-white truncate">{SUPPORT_EMAIL}</div>
                    </div>
                </a>
                <a href={`https://wa.me/${SUPPORT_WA}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link"
                    className="flex items-center gap-3 bg-slate-950 border border-slate-800 rounded-lg p-4 hover:border-[#00FF66]/60 transition-colors">
                    <div className="w-10 h-10 rounded-full bg-[#00FF66]/10 border border-[#00FF66]/40 flex items-center justify-center">
                        <MessageCircle className="w-4 h-4 text-[#00FF66]" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">WhatsApp</div>
                        <div className="text-sm text-white truncate">+91 95999 21411</div>
                    </div>
                </a>
            </div>

            {sent ? (
                <div className="text-center py-6" data-testid="contact-sent">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-[#00FF66] mb-3" />
                    <div className="text-white font-semibold">Message received</div>
                    <div className="text-sm text-slate-400 mt-1">We'll respond to {form.email}.</div>
                </div>
            ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
                    <h2 className="font-display text-lg font-bold uppercase text-white tracking-widest">Send us a message</h2>
                    <div>
                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Your Name</label>
                        <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="contact-name-input"
                            className="w-full mt-2 h-11 px-4 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                    </div>
                    <div>
                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Email</label>
                        <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                            data-testid="contact-email-input"
                            className="w-full mt-2 h-11 px-4 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                    </div>
                    <div>
                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Message</label>
                        <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                            data-testid="contact-message-input"
                            className="w-full mt-2 px-4 py-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                    </div>
                    <button type="submit" disabled={sending} data-testid="contact-submit-btn"
                        className="w-full inline-flex items-center justify-center gap-2 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52] disabled:opacity-50">
                        <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send Message"}
                    </button>
                </form>
            )}
        </InfoPage>
    );
}
